import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { TextField, Button, Box, Typography, MenuItem, Select, InputLabel, FormControl, IconButton, Autocomplete, CircularProgress } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MarcaModal from './MarcaModal';
import { debounce } from 'lodash';
import FormCard from './FormCard';

const cores = [
  'Amarelo', 'Azul', 'Branco', 'Cinza', 'Dourado', 'Fantasia', 
  'Laranja', 'Marrom', 'Prata', 'Preto', 'Rosa', 'Roxo', 
  'Verde', 'Vermelho', 'Bege', 'Outra'
];

function VeiculoForm({ onVeiculoAdded, initialData, onCancelEdit }) {
  const { user } = useAuth();
  const [id, setId] = useState(null);
  const [empresa_id, setEmpresaId] = useState('');
  const [pessoa_id, setPessoaId] = useState('');
  const [placa, setPlaca] = useState('');
  const [marca_id, setMarcaId] = useState('');
  const [modelo, setModelo] = useState('');
  const [cor, setCor] = useState('');
  const [ano, setAno] = useState('');
  
  const [empresas, setEmpresas] = useState([]);
  // Autocomplete states for Pessoa
  const [pessoaOptionsVeiculo, setPessoaOptionsVeiculo] = useState([]);
  const [selectedPessoaVeiculo, setSelectedPessoaVeiculo] = useState(null);
  const [loadingPessoasVeiculo, setLoadingPessoasVeiculo] = useState(false);

  const [marcas, setMarcas] = useState([]);
  const [isMarcaModalOpen, setMarcaModalOpen] = useState(false);

  const fetchMarcas = () => {
    api.get('/marcas')
      .then(response => {
        setMarcas(response.data);
      })
      .catch(error => console.error('Erro ao buscar marcas:', error));
  };

  // Debounced fetch for Pessoas in VeiculoForm
  const debouncedFetchPessoasVeiculo = useMemo(() => 
    debounce((searchTerm) => {
      setLoadingPessoasVeiculo(true);
      api.get(`/pessoas?search=${searchTerm}`)
        .then(response => setPessoaOptionsVeiculo(response.data.data || response.data))
        .catch(error => console.error('Erro ao buscar pessoas para ve\u00EDculo:', error))
        .finally(() => setLoadingPessoasVeiculo(false));
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedFetchPessoasVeiculo.cancel();
    };
  }, [debouncedFetchPessoasVeiculo]);

  useEffect(() => {
    fetchMarcas();

    if (user && user.tipo_usuario === 'admin') {
      api.get('/empresas')
        .then(response => setEmpresas(response.data.data || response.data))
        .catch(error => console.error('Erro ao buscar empresas:', error));
    }

    // --- NEW LOGIC FOR PERSON AUTOCOMPLETE ---
    const fetchAndSetSelectedPessoa = async (personId) => {
      if (personId) {
        try {
          const response = await api.get(`/pessoas?id=${personId}`);
          const item = (response.data.data || response.data)[0];
          if (item) {
            setSelectedPessoaVeiculo(item);
            setPessoaOptionsVeiculo([item]);
          }
        } catch (error) {
          console.error(`Erro ao buscar pessoa com ID ${personId}:`, error);
        }
      }
    };
    // --- END NEW LOGIC ---

    if (initialData) {
      setId(initialData.id);
      setEmpresaId(initialData.empresa_id);
      setPessoaId(initialData.pessoa_id || '');
      setPlaca(initialData.placa);
      setMarcaId(initialData.marca_id || '');
      setModelo(initialData.modelo || '');
      setCor(initialData.cor || '');
      setAno(initialData.ano || '');

      // Call the new fetch logic for person
      fetchAndSetSelectedPessoa(initialData.pessoa_id);

    } else {
      clearForm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, user]);

  const clearForm = () => {
    setId(null);
    setEmpresaId(user?.empresa_id || '');
    setPessoaId('');
    setPlaca('');
    setMarcaId('');
    setModelo('');
    setCor('');
    setAno('');
    // Clear Autocomplete states for Pessoa
    setSelectedPessoaVeiculo(null);
    setPessoaOptionsVeiculo([]);
  };

  const handleMarcaSelecionada = (novaMarcaId) => {
    fetchMarcas(); // Re-fetch a lista de marcas
    setMarcaId(novaMarcaId); // Seleciona a nova marca no dropdown
    setMarcaModalOpen(false); // Fecha o modal
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const veiculoData = {
      empresa_id: user.tipo_usuario === 'admin' ? empresa_id : user.empresa_id,
      pessoa_id: pessoa_id || null,
      placa,
      marca_id: marca_id || null,
      modelo: modelo || null,
      cor: cor || null,
      ano: ano || null,
    };

    const apiCall = id ? api.put(`/veiculos/${id}`, veiculoData) : api.post('/veiculos', veiculoData);

    apiCall
      .then(response => { // Capture the response from the POST/PUT
        clearForm();
        if (!id) { // Only for creation (POST)
          const newVeiculoId = response.data.id; // Get the ID of the newly created vehicle
          if (newVeiculoId) {
            // Fetch the complete new vehicle object
            api.get(`/veiculos?id=${newVeiculoId}`)
              .then(veiculoResponse => {
                const newVeiculo = (veiculoResponse.data.data || veiculoResponse.data)[0]; // Assuming API returns array
                if (newVeiculo) {
                  onVeiculoAdded(newVeiculo); // Pass the new vehicle object to the callback
                } else {
                  console.error('Erro: Ve\u00EDculo rec\u00E9m-adicionado n\u00E3o encontrado.');
                  onVeiculoAdded(); // Call without data if not found
                }
              })
              .catch(error => {
                console.error('Erro ao buscar ve\u00EDculo rec\u00E9m-adicionado:', error);
                onVeiculoAdded(); // Call without data on error
              });
          } else {
            console.error('Erro: ID do ve\u00EDculo rec\u00E9m-adicionado n\u00E3o retornado.');
            onVeiculoAdded(); // Call without data if ID not returned
          }
        } else { // For update (PUT)
          onVeiculoAdded(); // Just refresh the list, no new item to select
          onCancelEdit();
        }
      })
      .catch(error => {
        console.error('Erro ao salvar ve\u00EDculo:', error);
      });
  };

  return (
    <>
      <MarcaModal 
        open={isMarcaModalOpen}
        onClose={() => setMarcaModalOpen(false)}
        onMarcaSelecionada={handleMarcaSelecionada}
      />
      <FormCard
        component="form"
        onSubmit={handleSubmit}
        sx={{
          mt: 2,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          pb: { xs: 8, sm: 9 },
        }}
      >
        <Typography variant="h5" gutterBottom>
          {id ? 'Editar Ve\u00EDculo' : 'Adicionar Novo Ve\u00EDculo'}
        </Typography>
          
          {user?.tipo_usuario === 'admin' && (
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="empresa-select-label">Empresa</InputLabel>
              <Select
                labelId="empresa-select-label"
                value={empresa_id}
                label="Empresa"
                onChange={(e) => setEmpresaId(e.target.value)}
              >
                {empresas.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>{emp.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Autocomplete
            id="pessoa-autocomplete-veiculo"
            options={pessoaOptionsVeiculo}
            getOptionLabel={(option) => option.nome || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedPessoaVeiculo}
            onChange={(event, newValue) => {
              setSelectedPessoaVeiculo(newValue);
              setPessoaId(newValue ? newValue.id : '');
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input') {
                if (newInputValue) {
                  debouncedFetchPessoasVeiculo(newInputValue);
                } else {
                  setPessoaOptionsVeiculo([]);
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pessoa (Propriet\u00E1rio)"
                variant="outlined"
                margin="normal"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPessoasVeiculo ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            label="Placa"
            variant="outlined"
            fullWidth
            value={placa}
            onChange={(e) => setPlaca(e.target.value)}
            margin="normal"
            required
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="marca-select-label">Marca</InputLabel>
              <Select
                labelId="marca-select-label"
                value={marca_id}
                label="Marca"
                onChange={(e) => setMarcaId(e.target.value)}
              >
                <MenuItem value=""><em>Nenhuma</em></MenuItem>
                {marcas.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton onClick={() => setMarcaModalOpen(true)} color="primary">
              <AddCircleOutlineIcon />
            </IconButton>
          </Box>

          <TextField
            label="Modelo"
            variant="outlined"
            fullWidth
            value={modelo}
            onChange={(e) => setModelo(e.target.value)}
            margin="normal"
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="cor-select-label">Cor</InputLabel>
            <Select
              labelId="cor-select-label"
              value={cor}
              label="Cor"
              onChange={(e) => setCor(e.target.value)}
            >
              <MenuItem value=""><em>Nenhuma</em></MenuItem>
              {cores.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Ano"
            variant="outlined"
            fullWidth
            type="number"
            value={ano}
            onChange={(e) => setAno(e.target.value)}
            margin="normal"
          />
        <Box
          sx={{
            position: 'sticky',
            bottom: 'max(16px, env(safe-area-inset-bottom, 0px) + 8px)',
            display: 'flex',
            gap: 1.5,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            mt: 2,
            pt: 2,
            pb: 1,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            zIndex: 1,
          }}
        >
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={onCancelEdit}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
          >
            {id ? 'Atualizar Ve\u00EDculo' : 'Adicionar Ve\u00EDculo'}
          </Button>
        </Box>
      </FormCard>
    </>
  );
}

export default VeiculoForm;

